import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  MarkerType,
  BaseEdge,
  EdgeLabelRenderer,
  getStraightPath,
  useInternalNode,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type EdgeProps,
  type InternalNode,
  type NodeProps,
  type ReactFlowInstance,
} from '@xyflow/react'
import clsx from 'clsx'
import { XIcon } from 'lucide-react'
import Spinner from '@tandem/ui/Spinner'
import { useTRPC, type RouterOutputs } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import { useAside } from '@/layouts/Main'
import '@xyflow/react/dist/style.css'
import $type from '@tandem/ui/type.css'
import $layout from '@tandem/ui/layout.css'
import $ from './-Schema.css'

export const Route = createFileRoute('/proexpert/schema')({
  component: SchemaPage,
})

type Graph = NonNullable<RouterOutputs['proexpert']['graph']['get']>
type GNode = Graph['nodes'][number]
type GEdge = Graph['edges'][number]
type AppNode = Node<{ node: GNode; w: number; dim?: boolean }, 'graph'>
type Selected = { kind: 'node'; node: GNode } | { kind: 'edge'; id: string; edge: GEdge }

// Роли таблиц в схеме (KNOWLEDGE_GRAPH.md), а не PERSON/ORG: раскладывают граф визуально.
let TYPE: Record<string, { ru: string; color: string }> = {
  entity: { ru: 'сущность', color: '#4f46e5' },
  document: { ru: 'документ', color: '#0d9488' },
  event: { ru: 'событие', color: '#d97706' },
  dictionary: { ru: 'справочник', color: '#64748b' },
  link: { ru: 'связка', color: '#7c3aed' },
  other: { ru: '—', color: '#6b7280' },
}
let TABLE_COLOR = '#94a3b8'

let colorOf = (n: GNode) => (n.kind === 'table' ? TABLE_COLOR : TYPE[n.type ?? 'other'].color)
let typeOf = (n: GNode) => (n.kind === 'table' ? 'таблица' : TYPE[n.type ?? 'other'].ru)
let sizeOf = (n: GNode) => ({ w: Math.min(260, Math.max(128, n.label.length * 7.5 + 52)), h: n.kind === 'concept' ? 54 : 46 })

let STATUS: Record<GEdge['status'], string> = {
  declared: 'объявлен (внешний ключ)',
  confirmed: 'подтверждён',
  open: 'открытый вопрос',
  rejected: 'отвергнут',
}

let edgeStroke = (e: GEdge) =>
  e.status === 'confirmed' ? '#059669'
  : e.status === 'rejected' ? '#e5e7eb'
  : e.kind === 'semantic' ? '#7c3aed'
  : e.kind === 'soft' ? '#d97706'
  : '#94a3b8'

let edgeDashed = (e: GEdge) => e.kind !== 'fk' && e.status !== 'confirmed'

let edgeLabel = (e: GEdge) =>
  e.kind === 'semantic' ? e.via
  : e.kind === 'soft' && e.overlap ? `${e.overlap.matched}/${e.overlap.total}`
  : ''

// Handles must exist for react-flow to register edge endpoints, but floating
// edges pick their own attach point on the node border — so hide the dots.
// Styled via `style`, never `class` — a class prop clobbers Handle's own
// className and breaks edge rendering.
let HANDLE = { opacity: 0, border: 0 }

function GraphNodeView({ data, selected }: NodeProps<AppNode>) {
  let n = data.node
  return (
    <div
      class={clsx($.node, n.kind === 'table' && $.tableNode, selected && $.nodeSelected)}
      style={{ width: data.w, opacity: data.dim ? 0.25 : 1 }}
    >
      <Handle type='target' position={Position.Left} style={HANDLE} />
      <span class={$.nodeDot} style={{ background: colorOf(n) }} />
      <div class={$.nodeBody}>
        <span class={$.nodeLabel}>{n.label}</span>
        <span class={$.nodeMeta}>{typeOf(n)}</span>
      </div>
      <Handle type='source' position={Position.Right} style={HANDLE} />
    </div>
  )
}

let nodeTypes = { graph: GraphNodeView }

type XY = { x: number; y: number }

// Border point of `node` on the segment towards `other` — the rhombus
// approximation from react-flow's floating-edges example.
let borderPoint = (node: InternalNode, other: InternalNode): XY => {
  let w = (node.measured.width || 200) / 2
  let h = (node.measured.height || 50) / 2
  let cx = node.internals.positionAbsolute.x + w
  let cy = node.internals.positionAbsolute.y + h
  let ox = other.internals.positionAbsolute.x + (other.measured.width || 200) / 2
  let oy = other.internals.positionAbsolute.y + (other.measured.height || 50) / 2
  let dx = (ox - cx) / (2 * w) - (oy - cy) / (2 * h)
  let dy = (ox - cx) / (2 * w) + (oy - cy) / (2 * h)
  let a = 1 / (Math.abs(dx) + Math.abs(dy) || 1)
  return { x: w * a * (dx + dy) + cx, y: h * a * (dy - dx) + cy }
}

// In a radial layout fixed left/right handles would make edges loop around the
// card; a floating edge attaches to whichever border faces the other endpoint,
// so spokes radiate naturally from the hub. Parallel edges between the same
// node pair fan out with per-edge curvature (data.bend) so their lines and
// labels don't collapse onto one straight segment.
function FloatingEdge({ id, source, target, markerEnd, style, label, data }: EdgeProps) {
  let sourceNode = useInternalNode(source)
  let targetNode = useInternalNode(target)
  if (!sourceNode || !targetNode) return null
  let s = borderPoint(sourceNode, targetNode)
  let t = borderPoint(targetNode, sourceNode)
  let bend = (data?.bend as number | undefined) ?? 0
  let path: string, lx: number, ly: number
  if (bend) {
    let len = Math.hypot(t.x - s.x, t.y - s.y) || 1
    let nx = -(t.y - s.y) / len, ny = (t.x - s.x) / len
    let mx = (s.x + t.x) / 2, my = (s.y + t.y) / 2
    // Quadratic control at 2×bend puts the curve's apex (and the label) at ×bend.
    path = `M ${s.x},${s.y} Q ${mx + nx * bend * 2},${my + ny * bend * 2} ${t.x},${t.y}`
    lx = mx + nx * bend; ly = my + ny * bend
  } else {
    ;[path, lx, ly] = getStraightPath({ sourceX: s.x, sourceY: s.y, targetX: t.x, targetY: t.y })
  }
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} />
      {label && (
        <EdgeLabelRenderer>
          <div class={$.edgeLabel} style={{ transform: `translate(-50%, -50%) translate(${lx}px, ${ly}px)` }}>{label}</div>
        </EdgeLabelRenderer>
      )}
    </>
  )
}

let edgeTypes = { floating: FloatingEdge }

// Radial layout: the busiest concept sits at the centre, a BFS tree fans out in
// concentric rings, and children pack inside the parent's angular wedge (sized
// by subtree leaf count) so each branch keeps to its own sector.
let layout = (nodes: GNode[], edges: GEdge[]): Map<string, XY> => {
  let byId = new Map(nodes.map(n => [n.id, n]))
  let deg = new Map<string, number>()
  let adj = new Map(nodes.map(n => [n.id, [] as string[]]))
  for (let e of edges) {
    if (e.src === e.dst) continue
    deg.set(e.src, (deg.get(e.src) ?? 0) + 1)
    deg.set(e.dst, (deg.get(e.dst) ?? 0) + 1)
    adj.get(e.src)!.push(e.dst)
    adj.get(e.dst)!.push(e.src)
  }
  let busiest = (a: string, b: string) => (deg.get(b) ?? 0) - (deg.get(a) ?? 0)
  let root = nodes.map(n => n.id).sort(busiest)[0]

  let children = new Map(nodes.map(n => [n.id, [] as string[]]))
  let depth = new Map([[root, 0]])
  for (let queue = [root]; queue.length; ) {
    let id = queue.shift()!
    for (let nb of [...new Set(adj.get(id))].sort(busiest))
      if (!depth.has(nb)) {
        depth.set(nb, depth.get(id)! + 1)
        children.get(id)!.push(nb)
        queue.push(nb)
      }
  }

  let leaves = new Map<string, number>()
  let count = (id: string): number => {
    let ch = children.get(id)!
    let c = ch.length ? ch.reduce((s, k) => s + count(k), 0) : 1
    leaves.set(id, c)
    return c
  }
  count(root)

  // Ring radius sized so the first ring has ~a card width of arc per node.
  let ring = Math.max(380, (children.get(root)!.length * 270) / (2 * Math.PI))
  let pos = new Map<string, XY>()
  let put = (id: string, x: number, y: number) => {
    let { w, h } = sizeOf(byId.get(id)!)
    pos.set(id, { x: x - w / 2, y: y - h / 2 })
  }
  let place = (id: string, a0: number, a1: number) => {
    let r = depth.get(id)! * ring
    let mid = (a0 + a1) / 2
    put(id, Math.cos(mid) * r, Math.sin(mid) * r)
    let a = a0
    for (let k of children.get(id)!) {
      let step = ((a1 - a0) * leaves.get(k)!) / leaves.get(id)!
      place(k, a, a + step)
      a += step
    }
  }
  place(root, -Math.PI / 2, 1.5 * Math.PI)

  // Isolated nodes (no edges at all) park to the left of the wheel.
  let maxDepth = Math.max(1, ...depth.values())
  nodes.filter(n => !pos.has(n.id)).forEach((n, i) => put(n.id, -(maxDepth * ring) - 340, -(maxDepth * ring) + i * 130))
  return pos
}

function SchemaPage() {
  let { company } = useAuth()
  let trpc = useTRPC()
  let enabled = company?.features?.includes('proexpert') ?? false
  let isAdmin = company?.role === 'admin'
  let { data: graph, isLoading } = useQuery({ ...trpc.proexpert.graph.get.queryOptions(), enabled })

  let [selected, setSelected] = useState<Selected | null>(null)
  let [hoverId, setHoverId] = useState<string | null>(null)
  let [positions, setPositions] = useState<Map<string, XY>>(new Map())
  let [rf, setRf] = useState<ReactFlowInstance<AppNode, Edge> | null>(null)
  // useNodesState/useEdgesState so react-flow owns measured dimensions — edges route off them.
  let [nodes, setNodes, onNodesChange] = useNodesState<AppNode>([])
  let [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])

  // The graph is calm at rest; colour and predicates are earned by attention. The
  // node you point at (or, absent that, the one you've selected) is the focus —
  // its subgraph lights up, everything else recedes.
  let focusId = hoverId ?? (selected?.kind === 'node' ? selected.node.id : null)

  // Top-level edges only: internal (both ends in one concept) is drill-down material.
  // Also drop any dangling endpoint so elk never sees an edge to a missing node.
  let topEdges = useMemo(() => {
    if (!graph) return []
    let ids = new Set(graph.nodes.map(n => n.id))
    return graph.edges.filter(e => !e.internal && ids.has(e.src) && ids.has(e.dst))
  }, [graph])
  // Relayout only when the node/edge SET changes, not on a mere verdict/colour change.
  let structure = useMemo(
    () => graph ? graph.nodes.map(n => n.id).join('|') + '#' + topEdges.map(e => `${e.src}>${e.dst}`).join('|') : '',
    [graph, topEdges],
  )

  useEffect(() => {
    setPositions(graph?.nodes.length ? layout(graph.nodes, topEdges) : new Map())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structure])

  useEffect(() => {
    setNodes((graph?.nodes ?? []).filter(n => positions.has(n.id)).map(n => ({
      id: n.id,
      type: 'graph',
      position: positions.get(n.id)!,
      data: { node: n, w: sizeOf(n).w },
    })))
  }, [graph, positions, setNodes])

  // Dim everything outside the focused node's neighbourhood. Map over the existing
  // nodes (spread preserves react-flow's measured size) so hovering never reflows.
  useEffect(() => {
    let active = new Set<string>()
    if (focusId) {
      active.add(focusId)
      for (let e of topEdges) {
        if (e.src === focusId) active.add(e.dst)
        if (e.dst === focusId) active.add(e.src)
      }
    }
    setNodes(nds => nds.map(n => {
      let dim = focusId ? !active.has(n.id) : false
      return n.data.dim === dim ? n : { ...n, data: { ...n.data, dim } }
    }))
  }, [focusId, topEdges, setNodes])

  useEffect(() => {
    // Fan out parallel edges of one node pair: each gets its own curvature,
    // symmetric around straight; sign is canonical per pair so opposite-direction
    // edges don't land on the same arc.
    let pairs = new Map<string, number[]>()
    topEdges.forEach((e, i) => {
      let k = [e.src, e.dst].sort().join('\t')
      pairs.set(k, [...(pairs.get(k) ?? []), i])
    })
    let bends = new Map<number, number>()
    for (let idx of pairs.values())
      idx.forEach((ei, j) =>
        bends.set(ei, (j - (idx.length - 1) / 2) * 42 * (topEdges[ei].src < topEdges[ei].dst ? 1 : -1)))

    setEdges(topEdges.map((e, i) => {
      let incident = !!focusId && (e.src === focusId || e.dst === focusId)
      let faded = !!focusId && !incident
      let stroke = incident ? edgeStroke(e) : '#c9ced6' // colour is earned by focus; grey at rest
      return {
        id: `e${i}`,
        type: 'floating',
        source: e.src,
        target: e.dst,
        data: { edge: e, bend: bends.get(i) ?? 0 },
        label: incident ? edgeLabel(e) || undefined : undefined,
        style: {
          stroke,
          strokeWidth: incident ? 2 : 1,
          strokeDasharray: edgeDashed(e) ? '5 4' : undefined,
          opacity: faded ? 0.12 : focusId ? 1 : 0.6,
          transition: 'stroke 0.2s, stroke-width 0.2s, opacity 0.2s',
        },
        markerEnd: { type: MarkerType.ArrowClosed, color: stroke, width: 13, height: 13 },
      }
    }))
  }, [topEdges, focusId, setEdges])

  useEffect(() => { if (rf && positions.size) rf.fitView({ padding: 0.2 }) }, [rf, positions])

  useAside(() => selected ? <DetailPanel selected={selected} isAdmin={isAdmin} onClose={() => setSelected(null)} /> : null, [selected, isAdmin])

  if (!enabled) return <div class={$layout.container}><p class={$type.muted}>Раздел недоступен.</p></div>

  let counts = graph
    ? { concepts: graph.nodes.filter(n => n.kind === 'concept').length, tables: graph.nodes.filter(n => n.kind === 'table').length }
    : null

  return (
    <div class={$.page}>
      <div class={$.canvas}>
        {isLoading ? (
          <div class={$.empty}><Spinner /></div>
        ) : !graph || !graph.nodes.length ? (
          <div class={$.empty}><span class={$type.muted}>Карта данных пока пуста.</span></div>
        ) : (
          <ReactFlow<AppNode, Edge>
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onInit={setRf}
            nodesConnectable={false}
            onNodeMouseEnter={(_, node) => setHoverId(node.id)}
            onNodeMouseLeave={() => setHoverId(null)}
            onNodeClick={(_, node) => setSelected({ kind: 'node', node: (node as AppNode).data.node })}
            onEdgeClick={(_, edge) => setSelected({ kind: 'edge', id: edge.id, edge: (edge.data as { edge: GEdge }).edge })}
            onPaneClick={() => { setSelected(null); setHoverId(null) }}
            minZoom={0.2}
            fitView
          >
            <Background gap={22} color='#f4f5f7' />
            <Controls showInteractive={false} />
            <div class={$.title}>
              <b>Карта данных</b>
              {counts && <span class={$type.muted}>{counts.concepts} понятий · {counts.tables} таблиц</span>}
            </div>
          </ReactFlow>
        )}
      </div>
    </div>
  )
}

function DetailPanel({ selected, isAdmin, onClose }: { selected: Selected; isAdmin: boolean; onClose: () => void }) {
  return (
    <div class={$.panel}>
      <button type='button' class={clsx('plain', $.panelClose)} onClick={onClose} aria-label='Закрыть'><XIcon size={16} /></button>
      {selected.kind === 'node' ? <NodeDetail node={selected.node} /> : <EdgeDetail edge={selected.edge} isAdmin={isAdmin} />}
    </div>
  )
}

function NodeDetail({ node }: { node: GNode }) {
  let members = node.kind === 'concept' ? (node.tables ?? []) : [node.label]
  return (
    <>
      <div class={$.panelHead}>
        <span class={$.nodeDot} style={{ background: colorOf(node) }} />
        <h4>{node.label}</h4>
      </div>
      <p class={$type.muted}>
        {typeOf(node)} · {node.rows.toLocaleString('ru')} строк{node.kind === 'concept' ? ` · ${members.length} табл.` : ''}
      </p>
      {node.description && <p>{node.description}</p>}
      {node.aliases?.length ? <p class={$type.small}><b>Синонимы:</b> {node.aliases.join(', ')}</p> : null}
      {node.note && <p class={$.note}>{node.note}</p>}
      <div class={$.tables}>
        {members.map(t => <TableProfile key={t} name={t} />)}
      </div>
    </>
  )
}

function TableProfile({ name }: { name: string }) {
  let trpc = useTRPC()
  let { data: table, isLoading } = useQuery(trpc.proexpert.graph.table.queryOptions({ name }))
  return (
    <details class={$.table}>
      <summary>{name}{table ? <span class={$type.muted}> · {table.columns.length} кол.</span> : null}</summary>
      {isLoading ? <Spinner /> : !table ? <p class={$type.muted}>—</p> : (
        <ul class={$.cols}>
          {table.columns.map(c => (
            <li key={c.name}>
              <span class={$.colName}>{c.name}</span>
              <span class={$.colType}>{c.dataType}</span>
              {c.isPk && <span class={$.tag}>PK</span>}
              {c.fkRef && <span class={$.tag}>→ {c.fkRef}</span>}
              {c.softRef && <span class={$.tagSoft}>~ {c.softRef}{c.softOverlap ? ` (${c.softOverlap.matched}/${c.softOverlap.total})` : ''}</span>}
              {c.values?.length ? <span class={$.colDomain}>{c.values.slice(0, 6).map(v => v.v).join(', ')}{c.values.length > 6 ? '…' : ''}</span> : null}
              {c.json?.keys.length ? <span class={$.colDomain}>{`{${c.json.keys.slice(0, 8).map(k => k.key).join(', ')}${c.json.keys.length > 8 ? '…' : ''}}`}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </details>
  )
}

function EdgeDetail({ edge, isAdmin }: { edge: GEdge; isAdmin: boolean }) {
  let trpc = useTRPC()
  let qc = useQueryClient()
  let { data: graph } = useQuery(trpc.proexpert.graph.get.queryOptions())
  let annotate = useMutation(trpc.proexpert.graph.annotate.mutationOptions({
    onSuccess: fresh => qc.setQueryData(trpc.proexpert.graph.get.queryKey(), fresh),
  }))
  let live = graph?.edges.find(e => e.path && e.path === edge.path) ?? edge
  let labelOf = (id: string) => graph?.nodes.find(n => n.id === id)?.label ?? id.replace(/^(concept|table):/, '')
  let kind = edge.kind === 'fk' ? 'внешний ключ' : edge.kind === 'soft' ? 'мягкая связь' : 'семантическая связь'
  let annotatable = isAdmin && edge.kind !== 'fk' && edge.path

  return (
    <>
      <h4>{labelOf(edge.src)} → {labelOf(edge.dst)}</h4>
      <p class={$type.muted}>{kind}{edge.kind === 'semantic' ? `: «${edge.via}»` : ''}</p>
      {edge.kind !== 'semantic' && <p class={$type.small}>{edge.via}</p>}
      {edge.overlap && <p class={$type.small}>Совпадение значений: {edge.overlap.matched}/{edge.overlap.total}</p>}
      <p class={$type.small}>Статус: {STATUS[live.status]}</p>
      {annotatable && (
        <div class={$.verdicts}>
          <button type='button' disabled={annotate.isPending} onClick={() => annotate.mutate({ path: edge.path!, verdict: 'confirmed' })}>Подтвердить</button>
          <button type='button' disabled={annotate.isPending} onClick={() => annotate.mutate({ path: edge.path!, verdict: 'rejected' })}>Отвергнуть</button>
          {live.status !== 'open' && (
            <button type='button' class={$type.muted} disabled={annotate.isPending} onClick={() => annotate.mutate({ path: edge.path!, verdict: null })}>Сбросить</button>
          )}
        </div>
      )}
    </>
  )
}
